﻿<script setup lang="ts">
    import { getStatus, OperationalStatusMessage, StatusResponse } from './helpers/api';
    import { onBeforeUnmount, ref } from 'vue';
    import { http } from './httpWrapper';
    import { longDate } from '@/formatters';
    import { IntervalState } from '@/shared/intervalState.ts';

    const STATUS_CHECK_INTERVAL = 60000; // Check for updates every 1 minute

    const status = ref<StatusResponse>();

    const storedIntervals = IntervalState.inject();

    onBeforeUnmount(() => {
        storedIntervals?.reloadPrompt.clearInterval();
    });

    function isFuture(message: OperationalStatusMessage): boolean {
        if (message.from === null)
            return false;

        return message.from > new Date().toISOString();
    }

    function titlePrefix(message: OperationalStatusMessage): string {
        switch (message.status) {
            case 1:
                return isFuture(message)
                    ? 'Upcoming reduced availability (readonly mode): '
                    : 'Reduced availability (readonly mode): ';
            default:
                return '';
        }
    }

    function getParagraphs(message: OperationalStatusMessage): string[] {
        const result = message.message ? [message.message] : [];

        if (message.from && message.to) {
            result.push(`Expected period of reduced availability: ${longDate(message.from)} UTC to ${longDate(message.to)} UTC.`);
        } else if (message.from) {
            if (isFuture(message)) {
                result.push(`Reduced availability starts ${longDate(message.from)} UTC.`);
            } else {
                result.push(`Reduced availability started ${longDate(message.from)} UTC.`);
            }
        } else if (message.to) {
            result.push(`Normal operations expected from ${longDate(message.to)} UTC.`);
        }

        return result;
    }

    async function init(): Promise<void> {
        storedIntervals?.statusCheck.setInterval(async () => {
            status.value = await getStatus(http);
        }, STATUS_CHECK_INTERVAL);
        status.value = await getStatus(http);
    }

    init();

</script>

<template>
    <b-notification
        v-for="(s, i) in status?.operationalStatuses"
        :key="i"
        type="is-danger"
        is-large
        has-icon
        icon="account-hard-hat-outline"
        :closable="false"
        class="is-justify-content-center">
        <p v-if="s.title">
            <b>{{ titlePrefix(s) + s.title }}</b>
        </p>
        <p
            v-for="(p, j) in getParagraphs(s)"
            :key="j">
            {{ p }}
        </p>
    </b-notification>
</template>

<style scoped>
article {
    background-color: var(--eq-midnight-sun-1);
    color: var(--eq-text-primary);
    margin: 4px !important;
}
</style>
